import { Component, ContentChild, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { lockScroll, unlockScroll } from '../shared/scroll-lock';
import { DeepLinkService } from '../shared/deep-link.service';
import { BoardService } from '../shared/board.service';
import { leaveSheetEntry, pushSheetEntry } from './sheet-history';

@Component({
  selector: 'app-popup-card',
  templateUrl: './popup-card.component.html',
})
export class PopupCardComponent implements OnInit, OnChanges, OnDestroy {
  /** Card body — projected via <ng-template #body> inside <app-popup-card> */
  @ContentChild('body', { read: TemplateRef }) bodyTpl!: TemplateRef<any>;
  /** Hover popover content */
  @Input() popupTpl?: TemplateRef<any>;
  /** Click/tap overlay content — falls back to popupTpl if not provided */
  @Input() tapTpl?: TemplateRef<any>;

  /** Extra CSS classes for the card border (e.g. 'border-danger') */
  @Input() cardClass = '';
  /** Max-width of the tap overlay card (e.g. '340px' or '900px'); phones always get a full-screen sheet */
  @Input() popupMaxWidth = '640px';
  /** Title in the overlay's header row (beside 🔗 and ✕; stays visible while the content scrolls). */
  @Input() popupTitle: string | null = null;
  /** No card around the body: the projected body is the trigger itself (e.g. a .dash-row list row). */
  @Input() bare = false;
  /**
   * Deep-link view path of the tap overlay, including its tab ('heizung/boiler', 'klima/room/knx-f470').
   * Opening / closing the overlay opens / closes that view in the URL, and a link (or back / forward)
   * to it opens the overlay — with the same tappedChange(true) as a tap, so lazy loading still runs.
   * Phones: a tap opens it in a history entry of its own, so Back closes it (sheet-history.ts).
   */
  @Input() linkId: string | null = null;
  /** Board chart id of the overlay's chart (board-catalog.ts): with an admin session a 📌 button puts it
   *  into the active Analyse slot (or takes it out; BoardService). */
  @Input() boardChart: string | null = null;
  /** Emits true when the tap overlay opens and false when it closes (e.g. to load its data lazily). */
  @Output() tappedChange = new EventEmitter<boolean>();

  hovered = false;
  tapped  = false;
  private _timer: any;
  private sub?: Subscription;

  constructor(private readonly link: DeepLinkService, private readonly zone: NgZone, readonly board: BoardService) {}

  ngOnInit(): void {
    this.sub = this.link.external$.subscribe(() => this.syncFromLink());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // a new id, or the overlay template arriving with the data: a pending link may open it now
    if (changes['linkId'] || changes['tapTpl'] || changes['popupTpl']) this.syncFromLink(true);
  }

  showHover(): void {
    clearTimeout(this._timer);
    this.hovered = true;
  }

  hideHover(): void {
    this._timer = setTimeout(() => { this.hovered = false; }, 150);
  }

  toggleTap(event: Event): void {
    event.stopPropagation();
    if (this.tapped) { this.closeTap(); return; }
    if (!this.hasOverlay) return;
    if (this.linkId) pushSheetEntry(this.linkId, this.link, this.zone);   // phones: Back closes the sheet
    this.open();
    if (this.linkId) this.link.openView(this.linkId);
  }

  /** A [bare] trigger (a list row) is a button for the keyboard: Enter / Space open its overlay. */
  onKey(event: Event): void {
    if (!this.bare) return;
    event.preventDefault();   // Space would scroll the page
    this.toggleTap(event);
  }

  closeTap(): void {
    if (!this.tapped) return;
    this.tapped = false;
    unlockScroll();
    this.tappedChange.emit(false);
    if (this.linkId) { this.link.closeView(this.linkId); leaveSheetEntry(this.linkId); }
  }

  private get hasOverlay(): boolean { return !!(this.tapTpl || this.popupTpl); }

  private open(): void {
    // without an overlay template there is nothing to show: no scroll lock either
    if (this.tapped || !this.hasOverlay) return;
    this.tapped = true;
    this.hovered = false;
    lockScroll();
    this.tappedChange.emit(true);
  }

  /** Follow the link: open if it names this overlay; close only on navigation (not on creation). */
  private syncFromLink(onlyOpen = false): void {
    if (!this.linkId) return;
    const want = this.link.isOpen(this.linkId);
    if (want && !this.tapped && this.hasOverlay) {
      // emitted during the parent's change detection on creation: defer so its handlers may change state
      Promise.resolve().then(() => { if (this.linkId && this.link.isOpen(this.linkId)) this.open(); });
    } else if (!want && this.tapped && !onlyOpen) {
      this.tapped = false;
      unlockScroll();
      this.tappedChange.emit(false);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this._timer);
    this.sub?.unsubscribe();
    if (this.tapped) unlockScroll();
  }
}
